import { useDispatch, useSelector } from "react-redux";

import { FilteredFolders } from "constants/forecasts.constants";
import { ForecastActivityType, TYPE_WELLS } from "constants/settings.constants";
import _ from "lodash";
import {
  setFilteredForecastFolders,
  setHasViewItemsChanged
} from "store/features";
import { RootState } from "store/rootReducer";
import {
  selectCheckedForecastKeys,
  selectCheckedForecasts,
  selectCheckedGlobalTypeWells,
  selectCheckedTypeWellKeys,
  selectCheckedTypeWells,
  selectExpandedForecastKeys,
  selectExpandedTypeWellKeys,
  useForecastStore
} from "stores/forecast";
import { v4 as uuidv4 } from "uuid";

import { ForecastFolder, UserArpsItem } from "models/UserArpsModel";

import { useTypeWellUpdater, useUndoRedo } from "components/arps/hooks";

import { useSelectedProject } from "../../projects/hooks";
import { useForecastDispatch } from "../context";
import { getNewTypeWellName } from "../utils";

export const useAddNewTypeWell = (type: ForecastActivityType, location: string) => {
  const dispatch = useDispatch();
  const forecastDispatch = useForecastDispatch();
  const { selectedProject: project } = useSelectedProject();

  const undoRedoManager = useUndoRedo();
  const { changeSelectedTypeWell } = useTypeWellUpdater(undoRedoManager);

  const filteredForecastFolders = useSelector(
    (state: RootState) =>
      state.arps[
        type === TYPE_WELLS
          ? FilteredFolders.FilteredTypeWellFolders
          : FilteredFolders.FilteredForecastFolders
      ]
  );

  const checkedTypeWells = useForecastStore(selectCheckedTypeWells);
  const checkedForecastsList = useForecastStore(selectCheckedForecasts);
  const checkedForecasts = type === TYPE_WELLS ? checkedTypeWells : checkedForecastsList;

  const checkedTypeWellKeys = useForecastStore(selectCheckedTypeWellKeys);
  const checkedForecastKeys = useForecastStore(selectCheckedForecastKeys);
  const checkedKeys = type === TYPE_WELLS ? checkedTypeWellKeys : checkedForecastKeys;

  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const fsSetCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const setTemporaryForecast = useForecastStore((s) => s.setTemporaryForecast);
  const setExpandedTypeWellKeys = useForecastStore((s) => s.setExpandedTypeWellKeys);
  const setExpandedForecastKeys = useForecastStore((s) => s.setExpandedForecastKeys);
  const setCheckedTypeWells = useForecastStore((s) => s.setCheckedTypeWells);
  const setCheckedForecasts = useForecastStore((s) => s.setCheckedForecasts);
  const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const setCheckedForecastKeys = useForecastStore((s) => s.setCheckedForecastKeys);

  const expandedTypeWellKeys = useForecastStore(selectExpandedTypeWellKeys);
  const expandedForecastKeys = useForecastStore(selectExpandedForecastKeys);
  const expandedKeys = type === TYPE_WELLS ? expandedTypeWellKeys : expandedForecastKeys;

  const addNewTypeWell = async (
    tw: ForecastFolder,
    currentForecastNameField?: string
  ) => {
    if (!tw?.isFolder) {
      return;
    }

    const folderCopy = _.cloneDeep(tw);
    folderCopy.children = folderCopy.children.filter((child) => !child.isFolder);

    folderCopy.children = folderCopy.children.map((child) => {
      if (child.isEdit) {
        const updatedTitle = currentForecastNameField || child.title;
        return {
          ...child,
          isEdit: false,
          title: updatedTitle
        };
      }
      return child;
    });

    const twName = getNewTypeWellName(folderCopy.children);

    const key = uuidv4();
    forecastDispatch({ payload: { forecastNameField: twName } });

    const newTw = {
      arps: [],
      folderId: tw.folderId,
      id: key,
      reserveCategory: tw?.reserveCategory,
      title: twName,
      uniqueID: `${key}${tw?.reserveCategory ? " (" + tw?.reserveCategory + ")" : ""}`,
      type: "forecast",
      children: [],
      color: "#000",
      thickness: 4,
      wellData: undefined,
      constants: [],
      folderName: tw.folderName,
      isChecked: false,
      isEdit: true,
      isFolder: false,
      key: key,
      forecastArray: [],
      wellList: [],
      editLocation: location,
      projectId: project?.projectId ?? ""
    } as UserArpsItem;

    const folderIndex = filteredForecastFolders.findIndex(
      (item) => item.id === tw?.id && item.reserveCategory === tw?.reserveCategory
    );

    if (folderIndex < 0) {
      return;
    }

    setTemporaryForecast(newTw);
    const newTwFolder = _.cloneDeep(folderCopy);
    const newCheckedTw = [newTw];
    newTwFolder.children = folderCopy.children.concat(newCheckedTw);
    const newFolders = _.cloneDeep(filteredForecastFolders);
    newFolders.splice(folderIndex, 1, newTwFolder);

    dispatch(
      setFilteredForecastFolders({
        type: type,
        filteredForecastFolders: newFolders
      })
    );

    const folderToAddTypeWellTo = filteredForecastFolders[folderIndex];

    // Only add to expanded keys if not already expanded
    const updatedExpandedKeys = expandedKeys.includes(folderToAddTypeWellTo.id)
      ? expandedKeys
      : [...expandedKeys, folderToAddTypeWellTo.id];

    if (type === TYPE_WELLS) {
      setExpandedTypeWellKeys(updatedExpandedKeys);
    } else {
      setExpandedForecastKeys(updatedExpandedKeys);
    }

    const updatedCheckedForecasts = [...newCheckedTw, ...checkedForecasts];
    if (type === TYPE_WELLS) {
      setCheckedTypeWells(updatedCheckedForecasts);
    } else {
      setCheckedForecasts(updatedCheckedForecasts);
    }

    const updatedCheckedKeys = [newTw.id, ...checkedKeys];
    if (type === TYPE_WELLS) {
      setCheckedTypeWellKeys(updatedCheckedKeys);
    } else {
      setCheckedForecastKeys(updatedCheckedKeys);
    }

    if (type === TYPE_WELLS) {
      const updatedGlobalCheckedTypeWells = [...newCheckedTw, ...checkedGlobalTypeWells];
      fsSetCheckedGlobalTypeWells(updatedGlobalCheckedTypeWells);
    }

    changeSelectedTypeWell(newTw);

    dispatch(setHasViewItemsChanged(true));
  };

  return { addNewTypeWell };
};
